/**
 * Viewport Allocation Integration
 * 
 * Simplified horizontal allocation system.
 * Views are positioned left-to-right in a horizontal scroll container.
 */

import { viewManager } from './view-manager.js';
import { allocateViewHorizontally, allocateAllViews, findExistingView } from './horizontal-allocator.js';
import type { GridState } from '../types/square.js';
import { bus } from '../core/events.js';
import { log } from '../services/logging/index.js';

/**
 * Viewport Allocation Integration
 * 
 * Handles horizontal positioning of views.
 */
export class ViewportAllocationIntegration {
  private gridState: GridState | null = null;
  private layerElement: HTMLElement | null = null;

  constructor(
    gridState: GridState | null = null,
    layerElement: HTMLElement | null = null
  ) {
    this.gridState = gridState;
    this.layerElement = layerElement;

    // Initialize View Manager with layer element
    if (layerElement) {
      viewManager.setLayerElement(layerElement);
    }

    // Listen for view needs allocation
    bus.on('view.needs-allocation', () => {
      this.onViewRegistered();
    });

    // Listen for process view destruction
    bus.on('process.view.destroyed', () => {
      this.onViewDestroyed();
    });

    // Listen for grid state updates (for viewport size, not allocation)
    bus.on('state.grid', (payload) => {
      if (payload && typeof payload === 'object' && 'grid' in payload) {
        this.updateGridState(payload.grid as GridState);
      }
    });
    
    // Listen for window resize to recalculate positions
    window.addEventListener('resize', () => {
      this.handleResize();
    });
  }

  /**
   * Initialize with grid state and layer element
   */
  initialize(gridState: GridState, layerElement: HTMLElement): void {
    this.gridState = gridState;
    this.layerElement = layerElement;
    
    if (layerElement) {
      viewManager.setLayerElement(layerElement);
    }
  }

  /**
   * Update grid state (for viewport size tracking, not allocation)
   */
  updateGridState(gridState: GridState): void {
    this.gridState = gridState;
    // No allocation needed - views position themselves horizontally
  }

  /**
   * Main allocation function
   * 
   * Uses horizontal allocator to position views left-to-right.
   */
  allocate(forceReallocate: boolean = false): void {
    // Use horizontal allocator - no grid needed
    allocateAllViews();
    log.debug(`[ViewportAllocationIntegration] Horizontal allocation completed`);
  }

  /**
   * Handle view registration - allocate horizontally
   */
  onViewRegistered(): void {
    // Get the newly registered view
    const views = viewManager.getAllViews();
    const latestView = Array.from(views.values())[views.size - 1];
    
    if (latestView) {
      // Allocate the new view on the left
      allocateViewHorizontally(latestView);
      log.debug(`[ViewportAllocationIntegration] Allocated new view ${latestView.id} horizontally`);
    }
  }

  /**
   * Handle view destruction
   */
  onViewDestroyed(): void {
    // Reallocate remaining views to fill gaps
    allocateAllViews();
  }

  /**
   * Update view visibility for page (removed - no pagination)
   */
  updateViewVisibilityForPage(): void {
    // Pagination removed - all views are visible in horizontal scroll
  }
  
  /**
   * Handle window resize - recalculate all positions
   */
  private handleResize(): void {
    // Debounce resize events
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    
    this.resizeTimeout = setTimeout(() => {
      allocateAllViews();
      log.debug(`[ViewportAllocationIntegration] Recalculated positions after resize`);
    }, 150);
  }
  
  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
}

/**
 * Singleton instance
 */
let _viewportAllocationIntegration: ViewportAllocationIntegration | null = null;

/**
 * Initialize viewport allocation integration
 */
export function initializeViewportAllocationIntegration(
  gridState: GridState,
  layerElement: HTMLElement
): ViewportAllocationIntegration {
  if (!_viewportAllocationIntegration) {
    _viewportAllocationIntegration = new ViewportAllocationIntegration();
  }
  _viewportAllocationIntegration.initialize(gridState, layerElement);
  return _viewportAllocationIntegration;
}

/**
 * Get singleton instance
 */
export function getViewportAllocationIntegration(): ViewportAllocationIntegration {
  if (!_viewportAllocationIntegration) {
    _viewportAllocationIntegration = new ViewportAllocationIntegration();
  }
  return _viewportAllocationIntegration;
}

/**
 * Export singleton instance
 */
export const viewportAllocationIntegration = getViewportAllocationIntegration();
